import moment from 'moment';
import { CONSTANTS } from '../../constants/constants';

const { TABS, CREDENTIALS_SUB_TABS, CRYPTOGRAPHY_SUB_TABS } = CONSTANTS.REPORT;
const { ASSETS: ASSETS_MENU_ITEM } = CONSTANTS.SIDE_NAVBAR.MENU_ITEMS;

const CSV_EXPORT_DATE_FORMAT = 'YYYY-MM-DD';
const CSV_EXPORT_SECTION_SEPARATOR = '-';
const CSV_EXPORT_NAME_SEGMENT_SEPARATOR = '_';
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|\r\n\0]/g;
const MAX_CSV_EXPORT_FILENAME_BASE_LENGTH = 200;
const CONSECUTIVE_NAME_SEGMENT_SEPARATORS = new RegExp(`${CSV_EXPORT_NAME_SEGMENT_SEPARATOR}+`, 'g');
const LEADING_TRAILING_NAME_SEGMENT_SEPARATORS = new RegExp(`^${CSV_EXPORT_NAME_SEGMENT_SEPARATOR}|${CSV_EXPORT_NAME_SEGMENT_SEPARATOR}$`, 'g');

/** Short slug used in the downloaded file name */
export const CSV_TABLE_SLUGS = {
  VULN: 'vuln',
  SBOM: 'sbom',
  BIN: 'bin',
  MISCONFIG: 'misconfig',
  CRYPTO_CERTS: 'certs',
  CRYPTO_PUBKEYS: 'pubkeys',
  CRYPTO_PRIVKEYS: 'privkeys',
  CREDS_ACCOUNTS: 'accounts',
  CREDS_HASHES: 'hashes',
  CREDS_SECRETS: 'secrets',
  LICISSUES: 'licissues',
  ASSETS: 'assets',
  ACTIVITIES: 'activities',
  ACCESS_CONTROL: 'accessctrl'
};

/** POST /download-csv route segments for exports where the URL path cannot be inferred (e.g. settings tabs). */
export const CSV_EXPORT_API_ROUTES = {
  ACCESS_CONTROL: 'access-control'
};

const REPORT_TAB_EXPORT_ENTRIES = [
  { tab: TABS.VULNERABILITIES, apiRoute: 'vulnerabilities', tableSlug: CSV_TABLE_SLUGS.VULN },
  { tab: TABS.SBOM, apiRoute: 'sbom', tableSlug: CSV_TABLE_SLUGS.SBOM },
  { tab: TABS.BINARIES, apiRoute: 'binaries', tableSlug: CSV_TABLE_SLUGS.BIN },
  { tab: TABS.MISCONFIGURATIONS, apiRoute: 'misconfigurations', tableSlug: CSV_TABLE_SLUGS.MISCONFIG },
  { tab: TABS.LICENSE_ISSUES, apiRoute: 'license-issues', tableSlug: CSV_TABLE_SLUGS.LICISSUES },
  { tab: CRYPTOGRAPHY_SUB_TABS.CERTIFICATES, apiRoute: 'certificates', tableSlug: CSV_TABLE_SLUGS.CRYPTO_CERTS },
  { tab: CRYPTOGRAPHY_SUB_TABS.PUBLIC_KEYS, apiRoute: 'public-keys', tableSlug: CSV_TABLE_SLUGS.CRYPTO_PUBKEYS },
  { tab: CRYPTOGRAPHY_SUB_TABS.PRIVATE_KEYS, apiRoute: 'private-keys', tableSlug: CSV_TABLE_SLUGS.CRYPTO_PRIVKEYS },
  { tab: CREDENTIALS_SUB_TABS.USER_ACCOUNTS, apiRoute: 'user-accounts', tableSlug: CSV_TABLE_SLUGS.CREDS_ACCOUNTS },
  { tab: CREDENTIALS_SUB_TABS.DETECTED_HASHES, apiRoute: 'detected-hashes', tableSlug: CSV_TABLE_SLUGS.CREDS_HASHES },
  { tab: CREDENTIALS_SUB_TABS.EXPOSED_SECRETS, apiRoute: 'exposed-secrets', tableSlug: CSV_TABLE_SLUGS.CREDS_SECRETS }
];

const NON_REPORT_TAB_EXPORT_ENTRIES = [
  { tab: ASSETS_MENU_ITEM, apiRoute: 'assets', tableSlug: CSV_TABLE_SLUGS.ASSETS },
  { tab: 'activities', apiRoute: 'activities', tableSlug: CSV_TABLE_SLUGS.ACTIVITIES },
  // Access Control has no URL segment to match, so its `tab` is unused for path inference; the entry only
  // exists to register the apiRoute -> tableSlug mapping resolved via the explicit customCSVExportRouteName.
  { tab: CSV_EXPORT_API_ROUTES.ACCESS_CONTROL, apiRoute: CSV_EXPORT_API_ROUTES.ACCESS_CONTROL, tableSlug: CSV_TABLE_SLUGS.ACCESS_CONTROL }
];

export function sanitizeFilenameSegment(value) {
  if (value == null || value === '') return '';
  return String(value)
    .trim()
    .replace(INVALID_FILENAME_CHARS, CSV_EXPORT_NAME_SEGMENT_SEPARATOR)
    .replace(/\s+/g, CSV_EXPORT_NAME_SEGMENT_SEPARATOR)
    .replace(CONSECUTIVE_NAME_SEGMENT_SEPARATORS, CSV_EXPORT_NAME_SEGMENT_SEPARATOR)
    .replace(LEADING_TRAILING_NAME_SEGMENT_SEPARATORS, '');
}

function normalizePathSegment(segment) {
  if (!segment) return '';
  try {
    return decodeURIComponent(segment).toLowerCase().trim();
  } catch {
    return segment.toLowerCase().trim();
  }
}

/** Turns an arbitrary path/route segment into a lowercase, dash-delimited slug. */
function slugifyRouteSegment(segment) {
  return normalizePathSegment(segment).replace(/\s+/g, CSV_EXPORT_SECTION_SEPARATOR);
}

function buildExportMaps(entries) {
  const pathSegmentToApiRoute = {};
  const pathSegmentToTableSlug = {};
  const customRouteToTableSlug = {};

  entries.forEach(({ tab, apiRoute, tableSlug }) => {
    const normalizedTab = normalizePathSegment(tab);
    pathSegmentToApiRoute[normalizedTab] = apiRoute;
    pathSegmentToTableSlug[normalizedTab] = tableSlug;
    customRouteToTableSlug[apiRoute] = tableSlug;
  });

  return { pathSegmentToApiRoute, pathSegmentToTableSlug, customRouteToTableSlug };
}

const {
  pathSegmentToApiRoute: PATH_SEGMENT_TO_API_ROUTE,
  pathSegmentToTableSlug: PATH_SEGMENT_TO_TABLE_SLUG,
  customRouteToTableSlug: CUSTOM_ROUTE_TO_TABLE_SLUG
} = buildExportMaps([...REPORT_TAB_EXPORT_ENTRIES, ...NON_REPORT_TAB_EXPORT_ENTRIES]);

function findPathSegmentMatch(pathname) {
  const segments = pathname.split('/').filter(Boolean).map(normalizePathSegment);
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];
    if (PATH_SEGMENT_TO_API_ROUTE[segment]) {
      return segment;
    }
  }
  return null;
}

/**
 * Resolves download API route and short table slug from URL or explicit override.
 */
export function resolveCsvExportRoute({ pathname, customCSVExportRouteName }) {
  if (customCSVExportRouteName) {
    const apiRoute = customCSVExportRouteName;
    const tableSlug =
      CUSTOM_ROUTE_TO_TABLE_SLUG[apiRoute] || PATH_SEGMENT_TO_TABLE_SLUG[normalizePathSegment(apiRoute)] || slugifyRouteSegment(apiRoute);
    return { apiRoute, tableSlug };
  }

  const matchedSegment = findPathSegmentMatch(pathname);
  if (matchedSegment) {
    return {
      apiRoute: PATH_SEGMENT_TO_API_ROUTE[matchedSegment],
      tableSlug: PATH_SEGMENT_TO_TABLE_SLUG[matchedSegment]
    };
  }

  const fallbackSegment = slugifyRouteSegment(pathname.split('/').at(-1));
  return {
    apiRoute: fallbackSegment,
    tableSlug: fallbackSegment
  };
}

/**
 * Default CSV export file name: [group-]asset-tableSlug-export-YYYY-MM-DD.csv
 * Hyphens separate major sections; underscores only replace spaces within group/asset names.
 */
export function buildDefaultCsvExportFilename({ groupName, assetName, tableSlug, date = moment().format(CSV_EXPORT_DATE_FORMAT) }) {
  const parts = [];

  const safeGroup = sanitizeFilenameSegment(groupName);
  const safeAsset = sanitizeFilenameSegment(assetName);

  if (safeGroup) parts.push(safeGroup);
  if (safeAsset) parts.push(safeAsset);
  if (tableSlug) parts.push(tableSlug);
  parts.push('export', date);

  return `${parts.join(CSV_EXPORT_SECTION_SEPARATOR)}.csv`;
}

export function getCsvExportDownloadUrl(apiRoute) {
  return `download-csv/${apiRoute}`;
}

/**
 * Builds a representative CSV file name for the dashboard chart click-through drawer tables.
 * Encodes org, chart, and the specific inner table so a downloaded file is self-describing even
 * with multiple orgs / charts / nested tables:
 *   [org-]chart[-innerTable]-export-YYYY-MM-DD.csv
 * e.g. "acme-corp-cisa-kev-associated-cves-export-2026-07-14.csv"
 */
export function buildDashboardCsvExportFilename({
  orgName,
  chartName,
  tableName,
  date = moment().format(CSV_EXPORT_DATE_FORMAT)
}) {
  const parts = [];

  const safeOrg = sanitizeFilenameSegment(orgName);
  const safeChart = sanitizeFilenameSegment(chartName);
  const safeTable = sanitizeFilenameSegment(tableName);

  if (safeOrg) parts.push(safeOrg);
  if (safeChart) parts.push(safeChart);
  if (safeTable) parts.push(safeTable);
  parts.push('export', date);

  // Lowercase + hyphen-delimited sections (underscores only from within sanitized names).
  return `${parts.join(CSV_EXPORT_SECTION_SEPARATOR).toLowerCase()}.csv`;
}


/**
 * Sanitizes user-edited export names before download. Falls back to the generated default
 * when the input is empty or becomes invalid after sanitization.
 */
export function finalizeCsvExportFileName(fileName, fallbackFileName) {
  const trimmed = String(fileName ?? '').trim();
  if (!trimmed) return fallbackFileName;

  const baseName = trimmed.replace(/\.csv$/i, '');
  const sanitized = sanitizeFilenameSegment(baseName);

  if (!sanitized) return fallbackFileName;

  const truncated = sanitized.slice(0, MAX_CSV_EXPORT_FILENAME_BASE_LENGTH).replace(/[._-]+$/, '');
  if (!truncated) return fallbackFileName;

  return `${truncated}.csv`;
}
